import React from 'react';
import { FileSignature, Monitor, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * E-SIGN Act consent block.
 * Mounted on the public quote page above the signature pad.
 * Discloses required-disclosure items under 15 U.S.C. §7001 (E-SIGN Act):
 *  - Right to receive a paper copy
 *  - Right to withdraw consent
 *  - Hardware/software requirements
 *  - Procedures to update contact info
 * Wired to a parent-controlled checkbox via `consented` + `onChange`.
 */
export function ESignConsent({ consented, onChange, quoteNumber, contractorEmail }) {
    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 sm:p-5" data-testid="esign-consent-block">
            <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-slate-900 text-amber-500 flex items-center justify-center shrink-0">
                    <FileSignature className="h-4 w-4" />
                </div>
                <div>
                    <h3 className="font-display text-base text-slate-900">Electronic-Signature Consent <span className="text-xs font-normal text-slate-500">(E-SIGN Act)</span></h3>
                    <p className="text-sm text-slate-700 mt-1">
                        Please review the following disclosures before signing electronically. By checking the box and signing in the pad below, you affirmatively consent to use electronic records and signatures to enter into the agreement reflected in Quote {quoteNumber ? <span className="font-mono text-slate-900">{quoteNumber}</span> : 'this'}.
                    </p>
                </div>
            </div>

            <ul className="mt-4 space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span><b>Paper copy.</b> You have the right to receive a paper copy of the executed quote. To request one, email the contractor named on this quote{contractorEmail ? <> at <a href={`mailto:${contractorEmail}`} className="underline text-slate-900">{contractorEmail}</a></> : ''}, or you may download the PDF using the “PDF” button on this page.</span>
                </li>
                <li className="flex items-start gap-2">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span><b>Right to withdraw consent.</b> You may withdraw your consent to use electronic records at any time before you sign by closing this page. After you sign, withdrawal of consent does not affect the legal effect or enforceability of the executed quote.</span>
                </li>
                <li className="flex items-start gap-2">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span><b>Updating your records.</b> If you need to update the email address used to receive electronic records, contact the contractor directly using the contact details on the quote.</span>
                </li>
                <li className="flex items-start gap-2">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span><b>Hardware &amp; software requirements.</b> You need (i) a modern desktop or mobile web browser with cookies and JavaScript enabled, (ii) an active internet connection, (iii) a touchscreen, mouse, or stylus to draw a signature, (iv) the ability to read and download PDF files, and (v) the ability to receive emails at the address used to access this quote.</span>
                </li>
                <li className="flex items-start gap-2">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span><b>Scope of consent.</b> Your consent applies only to this transaction and the records associated with this quote. The contractor and QuotesFlow may store a copy of the signed quote and the date, time, IP address, and email address used to sign it as proof of execution.</span>
                </li>
            </ul>

            <label className="mt-4 flex items-start gap-2 text-sm text-slate-800 cursor-pointer select-none">
                <input
                    type="checkbox"
                    checked={!!consented}
                    onChange={(e) => onChange?.(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-amber-500 cursor-pointer"
                    data-testid="esign-consent-checkbox"
                />
                <span>
                    I have read the E-SIGN Act disclosures above and <b>consent</b> to use electronic records and signatures for this quote. I also acknowledge the <Link to="/terms" target="_blank" className="underline text-slate-900" data-testid="esign-terms-link">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="underline text-slate-900" data-testid="esign-privacy-link">Privacy Policy</Link>.
                </span>
            </label>
        </div>
    );
}
